import Breadcrumbs from "@/components/Breadcrumbs";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import { PageService } from "@/services/admin-services";
import { CreatePage, Page } from "@/services/admin-services/models";
import { LocaleService } from "@/services/global-services";
import { Language, Locale } from "@/services/global-services/models";
import { useAuth0 } from "@auth0/auth0-react";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
    alpha,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    FormGroup,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { parseJSON } from "date-fns";
import { groupBy } from "lodash";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const breadcrumbs = [
    {
        to: "..",
        title: "Administration",
    },
    {
        title: "Content",
        to: "",
    },
    {
        title: "Pages",
        to: "",
    },
];

interface NestedPage extends Page {
    path: string;
    level: number;
    circularRef?: boolean;
}

const Overview = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [pages, setPages] = useState<Array<Page>>([]);
    const [groupedPages, setGroupedPages] = useState<Record<string, Array<NestedPage>>>();
    const [languages, setLanguages] = useState<Array<Language>>([]);

    const [deletePage, setDeletePage] = useState<string | null>(null);
    const [showWarning, setShowWarning] = useState<boolean>(false);
    const [clone, setClone] = useState<string | null>(null);

    const [openInNewTab, setOpenInNewTab] = useState<boolean>(true);

    const pageService = useMemo(() => new PageService("int"), []);

    const { user } = useAuth0();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    useEffect(() => {
        Promise.all([LocaleService.get().then(parseLanguages), pageService.getPages().then(setPages)]).then(() =>
            setLoading(false)
        );
    }, [pageService]);

    useEffect(() => {
        if (!pages || pages.length === 0) return;

        const nestedPages = [...pages] as Array<NestedPage>;
        nestedPages.forEach((page) => {
            let url = `${page.parentOrder}-${page.slug}`;
            const parents = [];
            let parent = pages.find((x) => x.id === page.parentPageId);
            let level = 0;

            parents.push(parent);

            while (parent) {
                url = `${parent.parentOrder}-${parent.slug}/${url}`;
                level++;
                const parentId = parent.parentPageId;
                parent = pages.find((x) => x.id === parentId);
            
                if (parent) {
                    if (parents.findIndex((x) => x && x.id === parent!.id) > -1) {
                        console.warn("Detected circular parent reference for page:", page);
                        page.circularRef = true;
                        break;
                    }

                    parents.push(parent);
                }
            }

            page.path = url;
            page.level = level;
        });

        setGroupedPages(groupBy(nestedPages, (x) => x.path));
    }, [pages]);

    const parseLanguages = (regions: Array<Locale>) => {
        const languages: Array<Language> = [];
        for (let i = 0; i < regions.length; i++) {
            const region = regions[i];

            for (let j = 0; j < region.languages.length; j++) {
                const language = region.languages[j];
                if (languages.findIndex((x) => x.label === language.label) > -1) continue;

                const split = language.slug.split("-");
                languages.push({ ...language, slug: `int-${split[1]}`, regionLabel: "International" });
            }
        }

        setLanguages(languages.sort((a, b) => a.label.localeCompare(b.label)));
    };

    const renderStatus = (status?: string) => {
        if (!status) {
            return (
                <Typography color={"text.secondary"} variant={"subtitle2"}>
                    -
                </Typography>
            );
        }

        let statusColor = theme.palette.success;
        if (status === "Draft") statusColor = theme.palette.warning;
        else if (status === "Old") statusColor = theme.palette.info;

        return (
            <Typography
                variant={"caption"}
                fontWeight={700}
                sx={{
                    bgcolor: alpha(statusColor.light, 0.1),
                    color: statusColor.dark,
                    paddingX: 1.5,
                    paddingY: 0.5,
                    borderRadius: 4,
                    display: "inline",
                }}
            >
                {status}
            </Typography>
        );
    };

    const onClone = (id: string) => {
        setClone(id);

        pageService.getPage(id).then((page) => {
            if (!page) return;
            const timestamp = new Date().toLocaleString();

            pageService
                .createPage({
                    ...page,
                    title: `${page.title} (Copy - ${timestamp})`,
                    user: user!.name!,
                    translations: [],
                } as CreatePage)
                .then((newPage) => {
                    setPages([...pages, { ...newPage, language: page.language }]);
                    setClone(null);
                });
        });
    };

    const onDelete = (id: string) => setDeletePage(id);

    const onCancelDelete = () => setDeletePage(null);

    const onPerformDelete = () => {
        pageService.deletePage(deletePage!).then((success) => {
            if (success) {
                const pagesSet = pages.filter((x) => x.id !== deletePage!);
                setDeletePage(null);
                setPages(pagesSet);
            } else {
                setShowWarning(true);
            }
        });
    };

    const renderPageDetails = (page: NestedPage) => {
        const creationDate = parseJSON(page.created).toLocaleString("nl-be") || "-";
        const modifiedDate = parseJSON(page.modified).toLocaleString("nl-be") || "-";

        return (
            <>
                <TableCell>
                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                        {page.title}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                        {languages.find((x) => x.slug === page.language)?.label || "-"}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                        {creationDate}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                        {page.createdBy}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                        {modifiedDate !== creationDate ? modifiedDate : "-"}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                        {page.modifiedBy}
                    </Typography>
                </TableCell>
                <TableCell>{renderStatus(page.published ? "Published" : "Draft")}</TableCell>
                <TableCell sx={{ p: 0 }}>
                    <Box sx={{ display: "flex" }}>
                        <Button
                            component={Link}
                            color={"primary"}
                            sx={{ ml: "auto" }}
                            to={page.id}
                            target={openInNewTab ? "_blank" : "_self"}
                            disabled={Boolean(clone)}
                        >
                            Edit
                        </Button>
                        <Box sx={{ ml: 2, position: "relative" }}>
                            <Button color={"primary"} onClick={() => onClone(page.id)} disabled={Boolean(clone)}>
                                Clone
                            </Button>
                            {clone === page.id && (
                                <CircularProgress
                                    size={24}
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        marginTop: "-12px",
                                        marginLeft: "-12px",
                                    }}
                                />
                            )}
                        </Box>
                        <Button
                            color={"error"}
                            sx={{ ml: 2, mr: 1 }}
                            onClick={() => onDelete(page.id)}
                            disabled={Boolean(clone)}
                        >
                            Delete
                        </Button>
                    </Box>
                </TableCell>
            </>
        );
    };

    const renderPages = (slug: string) => {
        const group = groupedPages && groupedPages[slug];
        if (!group) return null;

        const pages = group.sort((a, b) => a.language.localeCompare(b.language));
        const firstPage = pages[0];
        return (
            <Fragment key={slug}>
                <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    <TableCell rowSpan={pages.length} component={"th"} scope="row" sx={{ verticalAlign: "top" }}>
                        <Typography color={"text.secondary"} variant={"subtitle2"} sx={{ pl: firstPage.level * 2 }}>
                            /{firstPage.slug}
                        </Typography>
                    </TableCell>
                    {renderPageDetails(pages[0])}
                </TableRow>
                {pages.slice(1).map((page) => (
                    <TableRow key={page.id}>{renderPageDetails(page)}</TableRow>
                ))}
            </Fragment>
        );
    };

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbs} />

            <Divider />

            <Grid container spacing={2} p={{ xs: 2, md: 4 }}>
                <Grid size={{ xs: 12, md: 12 }} sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                        component={Link}
                        to="new"
                        variant="contained"
                        startIcon={<PlusIcon className="h-5 w-5" />}
                        sx={{ ml: "auto" }}
                    >
                        Create New Page
                    </Button>
                </Grid>

                <Grid size={{ xs: 12, md: 12 }} sx={{ display: "flex", alignItems: "center" }}>
                    <FormGroup sx={{ ml: "auto" }}>
                        <FormControlLabel
                            control={
                                <Checkbox checked={openInNewTab} onChange={(e) => setOpenInNewTab(e.target.checked)} />
                            }
                            sx={{ mr: 0 }}
                            label={<Typography variant="body2">Edit in new tab</Typography>}
                        />
                    </FormGroup>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }} aria-label="simple table">
                            <TableHead sx={{ bgcolor: "alternate.main" }}>
                                <TableRow>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Slug
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Title
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Language
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Created
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Author
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Modified
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Modification Author
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Status
                                        </Typography>
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading || !groupedPages ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={9}
                                            sx={{
                                                height: 400,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", placeContent: "center" }}>
                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : Object.keys(groupedPages).length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={9}
                                            sx={{
                                                height: 400,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", placeContent: "center" }}>
                                                <Typography variant="body2">No pages available.</Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    Object.keys(groupedPages)
                                        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                                        .map((key) => renderPages(key))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            {deletePage && (
                <ConfirmDeleteDialog
                    open={Boolean(deletePage)}
                    title={"Delete page"}
                    message={"Are you sure you wish to delete this page? You can not undo this action!"}
                    onCancel={onCancelDelete}
                    onDelete={onPerformDelete}
                />
            )}

            <Dialog
                open={showWarning}
                onClose={() => setShowWarning(false)}
                fullWidth
                maxWidth={"sm"}
                fullScreen={fullScreen}
            >
                <DialogTitle>Unable to delete page</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText>
                        We were unable to delete this page because one or more child pages exist.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setShowWarning(false)}>
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Overview;
